/**
 * User detail — read view + role grant/revoke + suspend/restore.
 *
 * The role picker hides approval-requiring roles; granting those is a
 * maker-checker flow (Approvals page) so the UI doesn't expose a
 * footgun where a non-admin tries to grant platform_admin and gets a
 * 400 they don't understand.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ApiError,
  type IamRole,
  type IamUser,
  type IamUserDetail,
  type UpdateIamUserProfileRequest,
  type UpdateIamUserStatusRequest,
} from "@dfp/api-client";
import { Badge } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

export function UserDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const auth = useAuth();
  const queryClient = useQueryClient();
  const canWrite = (auth.user?.permissions ?? []).includes("iam.user.write");
  const canGrant = (auth.user?.permissions ?? []).includes("iam.role.grant");
  const canRevoke = (auth.user?.permissions ?? []).includes("iam.role.revoke");

  const detail = useQuery({
    queryKey: ["iam", "users", "detail", id],
    queryFn: () => api.get<IamUserDetail>(`/api/iam/users/${id}`),
  });
  const roles = useQuery({
    queryKey: ["iam", "roles"],
    queryFn: () => api.get<readonly IamRole[]>("/api/iam/roles"),
  });

  if (detail.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (detail.isError || !detail.data) {
    return <p className="text-sm text-red-700">Failed to load user.</p>;
  }
  const { user, roles: granted, effectivePermissions } = detail.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{user.displayName}</h1>
        <p className="text-xs font-mono text-slate-500">{user.id}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Identity</CardTitle>
            <Badge tone={user.type === "staff" ? "brand" : "neutral"}>{user.type}</Badge>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            {canWrite ? (
              <EditProfileForm
                user={user}
                onChanged={() =>
                  void queryClient.invalidateQueries({
                    queryKey: ["iam", "users", "detail", id],
                  })
                }
              />
            ) : (
              <>
                <Row k="Email" v={user.email ?? "—"} />
                <Row k="Phone" v={user.phone ?? "—"} />
              </>
            )}
            <div className="space-y-2 border-t border-slate-100 pt-2">
              <Row k="MFA enrolled" v={user.mfaEnrolled ? "yes" : "no"} />
              <Row
                k="Last login"
                v={user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : "never"}
              />
              <Row k="Created" v={new Date(user.createdAt).toLocaleString()} />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Account status</CardTitle>
            <StatusBadge status={user.status} />
          </CardHeader>
          <CardBody>
            {canWrite ? (
              <StatusForm
                currentStatus={user.status}
                userId={user.id}
                onChanged={() =>
                  void queryClient.invalidateQueries({
                    queryKey: ["iam", "users", "detail", id],
                  })
                }
              />
            ) : (
              <p className="text-sm text-slate-500">
                Status changes require{" "}
                <code className="rounded bg-slate-100 px-1">iam.user.write</code>.
              </p>
            )}
          </CardBody>
        </Card>
      </div>

      {/*
        Admin password RESET (set a new password without the old one) — only for
        OTHER users. On your own profile you use the self-service "My password"
        card below (change with your current password), so the two don't overlap.
      */}
      {canWrite && auth.user?.sub !== id ? (
        <Card>
          <CardHeader>
            <CardTitle>Password</CardTitle>
            <span className="text-xs text-slate-500">
              {user.mustChangePassword
                ? "User will be forced to change on next login."
                : "Issued — last changed by user"}
            </span>
          </CardHeader>
          <CardBody>
            <AdminResetPasswordForm
              userId={user.id}
              userHasPassword={user.email !== null}
              onChanged={() =>
                void queryClient.invalidateQueries({
                  queryKey: ["iam", "users", "detail", id],
                })
              }
            />
          </CardBody>
        </Card>
      ) : null}

      {/*
        Self-service password change — moved here from the top-bar header. Shown
        only on your OWN profile (auth.user.sub === route id), and NOT gated on
        iam.user.write: any signed-in user can rotate their own password.
      */}
      {auth.user?.sub === id ? (
        <Card>
          <CardHeader>
            <CardTitle>My password</CardTitle>
            <span className="text-xs text-slate-500">
              Change the password for your own account.
            </span>
          </CardHeader>
          <CardBody>
            <Link
              to="/auth/change-password"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-simuflex-navy hover:text-simuflex-green transition-colors"
            >
              Change my password →
            </Link>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Roles</CardTitle>
          <span className="text-xs text-slate-500">{granted.length} granted</span>
        </CardHeader>
        <CardBody className="space-y-3">
          {granted.length === 0 ? (
            <p className="text-sm text-slate-500">No roles granted.</p>
          ) : (
            <ul className="space-y-1">
              {granted.map((g) => (
                <li
                  key={g.key}
                  className="flex items-baseline justify-between gap-3 rounded-md border border-slate-200 px-3 py-2"
                >
                  <div>
                    <div className="text-sm font-medium text-slate-900">{g.key}</div>
                    <div className="text-xs text-slate-500">{g.description}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-slate-500">
                      granted {new Date(g.grantedAt).toLocaleDateString()}
                    </span>
                    {canRevoke ? (
                      <RevokeButton
                        userId={user.id}
                        roleKey={g.key}
                        onRevoked={() =>
                          void queryClient.invalidateQueries({
                            queryKey: ["iam", "users", "detail", id],
                          })
                        }
                      />
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {canGrant ? (
            <GrantRoleControl
              userId={user.id}
              currentRoleKeys={granted.map((g) => g.key)}
              roles={roles.data ?? []}
              onGranted={() =>
                void queryClient.invalidateQueries({
                  queryKey: ["iam", "users", "detail", id],
                })
              }
            />
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Effective permissions</CardTitle>
          <span className="text-xs text-slate-500">{effectivePermissions.length}</span>
        </CardHeader>
        <CardBody>
          {effectivePermissions.length === 0 ? (
            <p className="text-sm text-slate-500">No permissions (no roles granted).</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {effectivePermissions.map((p) => (
                <code
                  key={p}
                  className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700"
                >
                  {p}
                </code>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: IamUserDetail["user"]["status"] }): JSX.Element {
  switch (status) {
    case "active":
      return <Badge tone="success">active</Badge>;
    case "suspended":
      return <Badge tone="warning">suspended</Badge>;
    case "locked_out":
      return <Badge tone="danger">locked</Badge>;
    case "deleted":
      return <Badge tone="neutral">deleted</Badge>;
  }
}

function StatusForm({
  currentStatus,
  userId,
  onChanged,
}: {
  currentStatus: IamUserDetail["user"]["status"];
  userId: string;
  onChanged: () => void;
}): JSX.Element {
  const [next, setNext] = useState<"active" | "suspended" | "locked_out">(
    currentStatus === "deleted" ? "active" : currentStatus,
  );
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (body: UpdateIamUserStatusRequest) =>
      api.patch<IamUserDetail["user"]>(`/api/iam/users/${userId}/status`, body),
    onSuccess: () => {
      setReason("");
      onChanged();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Update failed");
      } else {
        setError("Network error");
      }
    },
  });

  const dirty = next !== currentStatus;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={next}
          onChange={(e) => setNext(e.target.value as "active" | "suspended" | "locked_out")}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="active">active</option>
          <option value="suspended">suspended</option>
          <option value="locked_out">locked_out</option>
        </select>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (≥10 chars)"
          className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <Button
          variant={next === "active" ? "primary" : "danger"}
          disabled={!dirty || mutation.isPending || reason.trim().length < 10}
          onClick={() => {
            setError(null);
            mutation.mutate({ status: next, reason: reason.trim() });
          }}
        >
          {mutation.isPending ? "Saving…" : "Apply"}
        </Button>
      </div>
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
    </div>
  );
}

function GrantRoleControl({
  userId,
  currentRoleKeys,
  roles,
  onGranted,
}: {
  userId: string;
  currentRoleKeys: readonly string[];
  roles: readonly IamRole[];
  onGranted: () => void;
}): JSX.Element {
  const [pick, setPick] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const granted = useMemo(() => new Set(currentRoleKeys), [currentRoleKeys]);
  const assignable = roles
    .filter((r) => !r.requiresApproval && !granted.has(r.key))
    .sort((a, b) => a.key.localeCompare(b.key));

  const mutation = useMutation({
    mutationFn: (roleKey: string) =>
      api.post<IamUserDetail>(`/api/iam/users/${userId}/roles`, { roleKey }),
    onSuccess: () => {
      setPick("");
      onGranted();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Grant failed");
      } else {
        setError("Network error");
      }
    },
  });

  if (assignable.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        All assignable roles already granted. Approval-requiring roles flow via{" "}
        <code className="rounded bg-slate-100 px-1">/approvals</code>.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3">
      <span className="text-xs font-medium text-slate-700">Grant role:</span>
      <select
        value={pick}
        onChange={(e) => setPick(e.target.value)}
        className="rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      >
        <option value="">— pick a role —</option>
        {assignable.map((r) => (
          <option key={r.key} value={r.key}>
            {r.key} · {r.permissionCount} perms
          </option>
        ))}
      </select>
      <Button
        disabled={pick === "" || mutation.isPending}
        onClick={() => {
          setError(null);
          mutation.mutate(pick);
        }}
      >
        {mutation.isPending ? "Granting…" : "Grant"}
      </Button>
      {error ? <span className="text-xs text-red-700">{error}</span> : null}
    </div>
  );
}

function RevokeButton({
  userId,
  roleKey,
  onRevoked,
}: {
  userId: string;
  roleKey: string;
  onRevoked: () => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      api.delete<IamUserDetail>(`/api/iam/users/${userId}/roles/${encodeURIComponent(roleKey)}`),
    onSuccess: () => onRevoked(),
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Revoke failed");
      } else {
        setError("Network error");
      }
    },
  });
  return (
    <div className="flex items-center gap-2">
      {error ? <span className="text-xs text-red-700">{error}</span> : null}
      <Button
        variant="ghost"
        disabled={mutation.isPending}
        onClick={() => {
          setError(null);
          mutation.mutate();
        }}
      >
        {mutation.isPending ? "…" : "Revoke"}
      </Button>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs uppercase tracking-wide text-slate-500">{k}</span>
      <span className="text-sm font-medium text-slate-900">{v}</span>
    </div>
  );
}

/**
 * Admin edit-profile form — displayName / email / phone. Builds a patch
 * of only the changed fields: a blanked email/phone is sent as `null`
 * (clear), an unchanged field is omitted (kept). Submit is disabled
 * until something changes. PATCH /api/iam/users/:id; invalidates the
 * user query on success.
 *
 * Mirrors the create form's field rules (displayName ≥2 chars; phone
 * E.164) so the client catches the obvious cases before the server's
 * Zod gate.
 */
function EditProfileForm({
  user,
  onChanged,
}: {
  user: IamUser;
  onChanged: () => void;
}): JSX.Element {
  const [displayName, setDisplayName] = useState(user.displayName);
  const [email, setEmail] = useState(user.email ?? "");
  const [phone, setPhone] = useState(user.phone ?? "");
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: UpdateIamUserProfileRequest) =>
      api.patch<IamUser>(`/api/iam/users/${user.id}`, body),
    onSuccess: () => {
      setError(null);
      setShowSuccess(true);
      onChanged();
      window.setTimeout(() => setShowSuccess(false), 4000);
    },
    onError: (err: unknown) => {
      setShowSuccess(false);
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Update failed");
      } else {
        setError("Network error");
      }
    },
  });

  // Normalise current vs original. Empty string in a field means "clear"
  // (→ null) relative to a previously-set value; if it was already
  // null/empty, it stays omitted.
  const trimmedName = displayName.trim();
  const trimmedEmail = email.trim();
  const trimmedPhone = phone.trim();
  const nameChanged = trimmedName !== user.displayName;
  const emailChanged = trimmedEmail !== (user.email ?? "");
  const phoneChanged = trimmedPhone !== (user.phone ?? "");
  const dirty = nameChanged || emailChanged || phoneChanged;

  function buildPatch(): UpdateIamUserProfileRequest {
    const patch: { displayName?: string; email?: string | null; phone?: string | null } = {};
    if (nameChanged) patch.displayName = trimmedName;
    if (emailChanged) patch.email = trimmedEmail.length > 0 ? trimmedEmail : null;
    if (phoneChanged) patch.phone = trimmedPhone.length > 0 ? trimmedPhone : null;
    return patch;
  }

  function submit(): void {
    setError(null);
    setShowSuccess(false);
    if (nameChanged && trimmedName.length < 2) {
      setError("Display name must be at least 2 characters.");
      return;
    }
    if (
      emailChanged &&
      trimmedEmail.length > 0 &&
      !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail)
    ) {
      setError("Enter a valid email address (or clear it).");
      return;
    }
    if (phoneChanged && trimmedPhone.length > 0 && !/^\+[1-9]\d{6,14}$/.test(trimmedPhone)) {
      setError("Phone must be E.164, e.g. +255700000000 (or clear it).");
      return;
    }
    mutation.mutate(buildPatch());
  }

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="text-xs uppercase tracking-wide text-slate-500">Display name</span>
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={120}
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-wide text-slate-500">Email</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={254}
          placeholder="—"
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-wide text-slate-500">Phone</span>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+255700000000"
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </label>
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
      {showSuccess ? <p className="text-xs text-emerald-700">Profile updated.</p> : null}
      <div className="flex justify-end">
        <Button disabled={!dirty || mutation.isPending} onClick={submit}>
          {mutation.isPending ? "Saving…" : "Save profile"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Admin force-reset password form. Sets a new password hash on the
 * target user AND flips must_change_password=true so the user is
 * gated into the change-password flow on next login. The typed password
 * is a one-shot onboarding credential; communicate it to the user
 * out-of-band (SMS / WhatsApp / in-person).
 *
 * Minimum 12 chars matches the server's adminResetPasswordSchema.
 * Don't echo the password back to the operator after submit — they
 * must capture it before clicking Reset.
 */
function AdminResetPasswordForm({
  userId,
  userHasPassword,
  onChanged,
}: {
  userId: string;
  userHasPassword: boolean;
  onChanged: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      api.patch<{ ok: true }>(`/api/iam/users/${userId}/password`, { newPassword: draft }),
    onSuccess: () => {
      setDraft("");
      setConfirm("");
      setError(null);
      setShowSuccess(true);
      onChanged();
      window.setTimeout(() => setShowSuccess(false), 5000);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Reset rejected");
      } else {
        setError("Network error");
      }
    },
  });
  if (!userHasPassword) {
    return (
      <p className="text-sm text-slate-500">
        This user has no email + password. They authenticate via phone+OTP only.
      </p>
    );
  }
  const matches = draft.length === 0 || draft === confirm;
  const longEnough = draft.length >= 12;
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Set a one-shot password for the user. They will be forced to change it on next login.
      </p>
      <label className="block text-sm">
        <span className="text-xs uppercase tracking-wide text-slate-500">New password</span>
        <input
          type="password"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          minLength={12}
          maxLength={128}
          autoComplete="new-password"
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-sm">
        <span className="text-xs uppercase tracking-wide text-slate-500">Confirm</span>
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          minLength={12}
          maxLength={128}
          autoComplete="new-password"
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
      {showSuccess ? (
        <p className="text-xs text-emerald-700">
          Reset OK. Share the new password with the user out-of-band; they must change it on next
          login.
        </p>
      ) : null}
      {!longEnough && draft.length > 0 ? (
        <p className="text-xs text-amber-700">Password must be at least 12 characters.</p>
      ) : null}
      {!matches ? <p className="text-xs text-amber-700">Passwords don&apos;t match.</p> : null}
      <Button
        variant="danger"
        disabled={mutation.isPending || !longEnough || !matches || draft.length === 0}
        onClick={() => {
          setError(null);
          setShowSuccess(false);
          mutation.mutate();
        }}
      >
        {mutation.isPending ? "Resetting…" : "Reset password"}
      </Button>
    </div>
  );
}
